import { useEffect } from "react";
import { useLocation, Link } from "wouter";
import {
  LayoutDashboard,
  Grid3X3,
  CalendarDays,
  Star,
  Shield,
  Bell,
  MapPin,
  ChevronDown,
  LogOut,
  Plus,
  Settings,
  Trophy,
  Users,
  Bot,
  FileText,
  Target,
  HeartPulse,
  Globe,
  Share2,
  ImageIcon,
  Package,
  Rocket,
  Store,
  Lock,
  Braces,
  Wrench,
  Layers,
  Gift,
  Megaphone,
  Radar,
  Crown,
} from "lucide-react";
import { trackEvent } from "@/hooks/useTracking";
import logoBlack from "@assets/TORK_Logo_Black_1779647609157.png";
import logoWhite from "@assets/TORK_Logo_White_1779647609157.png";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import type { Location, Notification } from "@shared/schema";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useLocationStore } from "@/lib/store";
import { useAuth } from "@/hooks/use-auth";
import { useRtdProgress } from "@/hooks/use-rtd-progress";

function GeogridCreditsWidget() {
  const { data, isLoading } = useQuery<{ balance: number; tierLimit: number; tier: string }>({
    queryKey: ["/api/billing/geogrid-credits"],
    refetchOnWindowFocus: true,
  });

  const balance = data?.balance ?? 0;
  const tierLimit = data?.tierLimit ?? 0;
  const pct = tierLimit > 0 ? Math.min(100, Math.max(0, Math.round((balance / tierLimit) * 100))) : 0;
  const overage = balance > tierLimit;

  return (
    <div className="mt-2 p-3 rounded-md bg-muted/50" data-testid="widget-geogrid-credits">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">GeoGrid Credits</span>
        <span className="font-semibold" data-testid="text-geogrid-credits-balance">
          {isLoading ? "—" : balance.toLocaleString()}
        </span>
      </div>
      <div className="w-full h-1.5 bg-muted rounded-full mt-2 overflow-hidden">
        <div
          className={`h-full rounded-full ${overage ? "bg-emerald-500" : "bg-primary"}`}
          style={{ width: `${overage ? 100 : pct}%` }}
        />
      </div>
      <p className="text-xs text-muted-foreground mt-1" data-testid="text-geogrid-credits-detail">
        {isLoading
          ? "Loading…"
          : tierLimit > 0
          ? `${balance.toLocaleString()} / ${tierLimit.toLocaleString()} this month`
          : `${balance.toLocaleString()} available`}
      </p>
    </div>
  );
}

const navItems = [
  { title: "Road to Domination", url: "/road-to-domination", icon: Rocket },
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "GeoGrid Tracking", url: "/geogrid", icon: Grid3X3 },
  { title: "GBP Optimization", url: "/gbp-optimization", icon: Target },
  { title: "Profile Essentials", url: "/profile-health", icon: HeartPulse },
  { title: "Reviews", url: "/reviews", icon: Star },
  { title: "Media", url: "/media", icon: ImageIcon },
  { title: "Posts Engine", url: "/posts", icon: CalendarDays },
  { title: "Product Forge", url: "/products", icon: Package },
  { title: "Social Fortress", url: "/social", icon: Share2 },
  { title: "Reports", url: "/reports", icon: FileText },
  { title: "Leaderboard", url: "/leaderboard", icon: Trophy },
  { title: "Locations", url: "/clients", icon: Users },
  { title: "Plans & Pricing", url: "/pricing", icon: Crown },
];

const marketplaceItems = [
  { title: "Citations", url: "/citations", icon: Globe },
  { title: "Schema", url: "/schema", icon: Braces },
  { title: "Services", url: "/services", icon: Wrench },
  { title: "Website", url: "/website", icon: Layers },
  { title: "Press Release", url: "/press-release", icon: Megaphone },
  { title: "Listing Protection", url: "/listing", icon: Shield },
  { title: "Scan Credits", url: "/scan-credits", icon: Radar },
  { title: "Refer Friends", url: "/referrals", icon: Gift },
  { title: "Automation", url: "/automation", icon: Bot },
];

export function AppSidebar() {
  const [location, navigate] = useLocation();
  const { selectedLocationId, setSelectedLocationId } = useLocationStore();
  const { logout, user } = useAuth();
  const { isNavUnlocked } = useRtdProgress();

  const { data: locations } = useQuery<Location[]>({
    queryKey: ["/api/locations"],
  });

  const { data: notifications } = useQuery<Notification[]>({
    queryKey: ["/api/notifications/unread"],
  });

  const unreadCount = notifications?.filter((n) => !n.isRead).length || 0;
  useEffect(() => {
    if (locations?.length && (!selectedLocationId || !locations.find((l) => l.id === selectedLocationId))) {
      setSelectedLocationId(locations[0].id);
    }
  }, [locations, selectedLocationId]);

  const selectedLocation = locations?.find((l) => l.id === selectedLocationId) || locations?.[0];

  return (
    <Sidebar>
      <SidebarHeader className="p-4">
        <div className="flex items-center gap-2">
          <img src={logoBlack} alt="Tork" className="h-32 block dark:hidden" data-testid="logo-sidebar" />
          <img src={logoWhite} alt="Tork" className="h-32 hidden dark:block" />
        </div>

        {locations && locations.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" className="w-full justify-between mt-3 text-xs" data-testid="button-location-switcher">
                <span className="flex items-center gap-2 truncate">
                  <MapPin className="w-3 h-3 shrink-0" />
                  <span className="truncate">{selectedLocation?.name || "Select Location"}</span>
                </span>
                <ChevronDown className="w-3 h-3 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {locations.map((loc) => (
                <DropdownMenuItem
                  key={loc.id}
                  onClick={() => setSelectedLocationId(loc.id)}
                  data-testid={`menu-item-location-${loc.id}`}
                >
                  <MapPin className="w-3 h-3 mr-2" />
                  <span className="truncate">{loc.name}</span>
                  {loc.id === selectedLocationId && (
                    <Badge variant="secondary" className="ml-auto text-xs">Active</Badge>
                  )}
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem
                onClick={() => navigate("/setup")}
                data-testid="menu-item-add-location"
              >
                <Plus className="w-3 h-3 mr-2" />
                <span>Add Location</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => {
                const unlocked = isNavUnlocked(item.url);
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild={unlocked}
                      isActive={
                        unlocked && (
                          item.url === "/dashboard"
                            ? location === "/dashboard" || location === "/"
                            : location === item.url
                        )
                      }
                    >
                      {unlocked ? (
                        <Link href={item.url} data-testid={`link-nav-${item.title.toLowerCase().replace(/\s+/g, '-')}`} className={item.indent ? "pl-7" : ""}
                          onClick={() => trackEvent('nav_tab_clicked', { tab_name: item.title, previous_tab: location })}>
                          <item.icon className={`w-4 h-4 ${item.indent ? "w-3.5 h-3.5" : ""}`} />
                          <span className={item.indent ? "text-[13px]" : ""}>{item.title}</span>
                        </Link>
                      ) : (
                        <div className="flex items-center gap-2 opacity-30 cursor-not-allowed select-none" data-testid={`link-nav-${item.title.toLowerCase().replace(/\s+/g, '-')}-locked`}>
                          <item.icon className="w-4 h-4" />
                          <span className="flex-1">{item.title}</span>
                          <Lock className="w-3 h-3" />
                        </div>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="flex items-center gap-1.5">
            <Store className="w-3.5 h-3.5" />
            Market Place
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {marketplaceItems.map((item) => {
                const unlocked = isNavUnlocked(item.url);
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild={unlocked}
                      isActive={unlocked && location === item.url}
                    >
                      {unlocked ? (
                        <Link href={item.url} data-testid={`link-nav-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                          onClick={() => trackEvent('nav_tab_clicked', { tab_name: item.title, previous_tab: location })}>
                          <item.icon className="w-4 h-4" />
                          <span>{item.title}</span>
                        </Link>
                      ) : (
                        <div className="flex items-center gap-2 opacity-30 cursor-not-allowed select-none" data-testid={`link-nav-${item.title.toLowerCase().replace(/\s+/g, '-')}-locked`}>
                          <item.icon className="w-4 h-4" />
                          <span className="flex-1">{item.title}</span>
                          <Lock className="w-3 h-3" />
                        </div>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-4">
        <Link href="/notifications">
          <Button variant="ghost" className="w-full justify-start gap-2" data-testid="link-notifications">
            <Bell className="w-4 h-4" />
            <span>Notifications</span>
            {unreadCount > 0 && (
              <Badge className="ml-auto">{unreadCount}</Badge>
            )}
          </Button>
        </Link>
        <Link href="/settings">
          <Button variant="ghost" className="w-full justify-start gap-2" data-testid="link-settings">
            <Settings className="w-4 h-4" />
            <span>Settings</span>
          </Button>
        </Link>
        {user?.role === "admin" && (
          <Link href="/admin">
            <Button variant="ghost" className="w-full justify-start gap-2 text-red-400 hover:text-red-300" data-testid="link-admin">
              <Shield className="w-4 h-4" />
              <span>Admin Panel</span>
            </Button>
          </Link>
        )}
        <GeogridCreditsWidget />
        <div className="mt-2 text-xs text-muted-foreground truncate px-2">
          {user?.email || user?.username}
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start gap-2 mt-1 text-muted-foreground hover:text-destructive"
          onClick={() => logout.mutate()}
          data-testid="button-logout"
        >
          <LogOut className="w-4 h-4" />
          <span>Log out</span>
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}
